const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? "http://localhost:8787";

const KEY_ACCESS_TOKEN  = "worker.accessToken";
const KEY_REFRESH_TOKEN = "worker.refreshToken";
const KEY_USER_ID       = "worker.userId";
const KEY_IS_ANONYMOUS  = "worker.isAnonymous";
const KEY_EMAIL         = "worker.email";
const KEY_LOCATION      = "worker.location";

export interface IAuthTokens {
    accessToken: string;
    refreshToken: string;
    userId: string;
    isAnonymous: boolean;
    email?: string | null;
}

export class WorkerClient {
    private static _instance: WorkerClient;

    static get instance(): WorkerClient {
        if (!WorkerClient._instance) WorkerClient._instance = new WorkerClient();
        return WorkerClient._instance;
    }

    get accessToken(): string | null  { return localStorage.getItem(KEY_ACCESS_TOKEN); }
    get refreshToken(): string | null { return localStorage.getItem(KEY_REFRESH_TOKEN); }
    get userId(): string | null       { return localStorage.getItem(KEY_USER_ID); }
    get isAnonymous(): boolean        { return localStorage.getItem(KEY_IS_ANONYMOUS) === "true"; }
    get email(): string | null        { return localStorage.getItem(KEY_EMAIL); }
    get authenticated(): boolean      { return this.accessToken !== null; }
    get location(): { regionId: number; tileId: number } | null {
        const raw = localStorage.getItem(KEY_LOCATION);
        if (!raw) return null;
        try {
            return JSON.parse(raw) as { regionId: number; tileId: number };
        } catch {
            localStorage.removeItem(KEY_LOCATION);
            return null;
        }
    }

    storeTokens(data: IAuthTokens): void {
        localStorage.setItem(KEY_ACCESS_TOKEN,  data.accessToken);
        localStorage.setItem(KEY_REFRESH_TOKEN, data.refreshToken);
        localStorage.setItem(KEY_USER_ID,       data.userId);
        localStorage.setItem(KEY_IS_ANONYMOUS,  String(data.isAnonymous));
        if (data.email != null) {
            localStorage.setItem(KEY_EMAIL, data.email);
        } else {
            localStorage.removeItem(KEY_EMAIL);
        }
    }

    storeLocation(location: { regionId: number; tileId: number }): void {
        if (location?.regionId !== undefined) {
            localStorage.setItem(KEY_LOCATION, JSON.stringify(location));
        }
    }

    clearTokens(): void {
        [KEY_ACCESS_TOKEN, KEY_REFRESH_TOKEN, KEY_USER_ID, KEY_IS_ANONYMOUS, KEY_EMAIL, KEY_LOCATION]
            .forEach(k => localStorage.removeItem(k));
    }

    async get<T>(path: string, params?: Record<string, string>): Promise<T> {
        const url = new URL(path, WORKER_URL);

        if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
        return this.request<T>(url.toString(), { method: "GET" });
    }

    async post<T>(path: string, body?: unknown): Promise<T> {
        return this.request<T>(`${WORKER_URL}${path}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body ?? {})
        });
    }

    private async request<T>(url: string, init: RequestInit, isRetry = false): Promise<T> {
        const headers: Record<string, string> = {
            ...(init.headers as Record<string, string> | undefined)
        };

        if (this.accessToken) {
            headers["Authorization"] = `Bearer ${this.accessToken}`;
        }

        const response = await fetch(url, { ...init, headers });

        const isAuthEndpoint = url.includes("/auth/");

        if (response.status === 401 && !isRetry && !isAuthEndpoint) {
            if (this.refreshToken) {
                const refreshed = await this.attemptRefresh();
                if (refreshed) return this.request<T>(url, init, true);
            } else {
                this.clearTokens();
            }
        }

        if (!response.ok) {
            const body = await response.json().catch(() => ({})) as Record<string, unknown>;

            throw new Error((body["error"] as string | undefined) ?? `HTTP ${response.status}`);
        }

        const text = await response.text();
        try {
            return JSON.parse(text) as T;
        } catch {
            throw new Error(`Invalid JSON from ${url}: ${text.slice(0, 120)}`);
        }
    }

    private async attemptRefresh(): Promise<boolean> {
        try {
            const response = await fetch(`${WORKER_URL}/auth/refresh`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refreshToken: this.refreshToken })
            });

            if (!response.ok) {
                this.clearTokens();
                return false;
            }

            const data = await response.json() as { accessToken: string; refreshToken?: string };
            localStorage.setItem(KEY_ACCESS_TOKEN, data.accessToken);
            if (data.refreshToken) localStorage.setItem(KEY_REFRESH_TOKEN, data.refreshToken);
            return true;
        } catch {
            this.clearTokens();
            return false;
        }
    }
}
